import {
  formatAltitude,
  formatLatitude,
  formatLongitude,
} from '@/features/geolocation/utils/coordinates';
import type { LocationValue } from '@/features/geolocation/types/location';
import type { UpdateSupplyPointRequest } from '../services/schemas';

type GeoPayload = Pick<UpdateSupplyPointRequest, 'latitude' | 'longitude' | 'altitude'>;

/** The exact patterns the backend enforces on these three fields. */
const BACKEND_PATTERNS = {
  latitude: /^-?\d{0,3}(?:\.\d{0,7})?$/,
  longitude: /^-?\d{0,4}(?:\.\d{0,7})?$/,
  altitude: /^-?\d{0,4}(?:\.\d{0,2})?$/,
} as const;

/**
 * A rejection here would be a programming error, not user input — the values come
 * from clamped, wrapped numbers. It is worth shouting about, because a 400 on
 * these fields cannot attach to a form field: `appFormOptions` matches server
 * error keys against `defaultValues`, and our form key is `location`, so such an
 * error only reaches the generic `<FormErrors>` banner.
 */
function assertBackendPatterns(payload: GeoPayload) {
  for (const [field, pattern] of Object.entries(BACKEND_PATTERNS)) {
    const value = payload[field as keyof GeoPayload];
    if (typeof value === 'string' && !pattern.test(value)) {
      console.error(`Supply point ${field} "${value}" does not match the backend pattern.`);
    }
  }
}

/**
 * Numbers live in form state (natural for leaflet and `NumberField`); the API
 * takes constraint-satisfying decimal strings with a dot separator.
 */
export function toSupplyPointGeoPayload(value: LocationValue): GeoPayload {
  // Never touched -> omit the keys entirely so a PATCH does not write nulls.
  if (value.latitude === null && value.longitude === null && value.source === null) return {};

  // The user explicitly cleared an existing position -> write nulls.
  if (value.latitude === null || value.longitude === null) {
    return { latitude: null, longitude: null, altitude: null };
  }

  const payload: GeoPayload = {
    latitude: formatLatitude(value.latitude),
    longitude: formatLongitude(value.longitude),
    altitude: value.altitude === null ? null : formatAltitude(value.altitude),
  };

  assertBackendPatterns(payload);

  return payload;
}
